import React, { useState, useRef, useEffect } from 'react';
import { useUser } from '../userContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import FormInput from '../games/FormInput';
import { notifyError } from '../utils/notify';

export default function Profile() {
  const { user } = useUser();
  const [firstName, setFirstName] = useState(user.firstName || '');
  const [lastName, setLastName] = useState(user.lastName || '');
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (user) {
      async function fetchUserProfile() {
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const data = userDoc.data();
          setFirstName(data.firstName);
          setLastName(data.lastName);
        }
      }
      fetchUserProfile();
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (user) {
      const userDocRef = doc(db, 'users', user.uid);
      try {
        await updateDoc(userDocRef, { firstName, lastName });
        setUnsavedChanges(false);
        setSaveSuccess(true);
      } catch (error) {
        console.error("Error updating profile: ", error);
        notifyError('Could not save profile. Please try again.');
      } finally {
        setTimeout(() => setSaveSuccess(false), 3000); // Clear success badge after 3 seconds
      }
    }
  };

  return (
    <div className="w-full px-8 py-6">
      <form onSubmit={handleSubmit}>
        <div className="space-y-12 sm:space-y-16">
          <div>
            <div className="flex items-center">
              <h2 className="text-lg font-semibold tracking-tight gch-text-primary">
                Account
              </h2>
              {unsavedChanges && (
                <span className="ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-red-50 border border-red-200 text-red-700">
                  Unsaved Changes
                </span>
              )}
              {saveSuccess && (
                <span className="ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-green-50 border border-green-200 text-green-700">
                  Changes Saved
                </span>
              )}
            </div>
            <p className="mt-1 max-w-2xl text-sm leading-6 font-medium gch-text-secondary">
              Change your personal information.
            </p>
            <div className="mt-5 space-y-4 mb-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <FormInput
                    label="First Name"
                    name="firstName"
                    maxLength={20}
                    mandatory
                    autoComplete="given-name"
                    valueOverride={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                      setUnsavedChanges(true);
                      setSaveSuccess(false);
                    }}
                  />
                </div>

                <div>
                  <FormInput
                    label="Last Name"
                    name="lastName"
                    maxLength={20}
                    mandatory
                    autoComplete="family-name"
                    valueOverride={lastName}
                    onChange={(e) => {
                      setLastName(e.target.value);
                      setUnsavedChanges(true);
                      setSaveSuccess(false);
                    }}
                  />
                </div>
              </div>

              <div className="sm:col-span-4">
                <FormInput
                  label="Email"
                  name="email"
                  type="email"
                  valueOverride={user.email}
                  readOnly
                  helperText="Please contact support to change email"
                />
              </div>
            </div>

            <div className="flex items-center justify-end">
              <button
                type="submit"
                disabled={!unsavedChanges}
                className="btn-admin-primary btn-admin-md w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
